import React from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@/components/ui';
import { AccountView } from './lib/mapAccount';
import { AccountsTab } from './lib/deriveRows';
import { accountsCsvText, accountsCsvFilename } from './lib/accountsCsv';

interface AccountsExportButtonProps {
  /** The rows currently on screen — already tab-scoped, searched, filtered and sorted. */
  rows: AccountView[];
  tab: AccountsTab;
}

/**
 * Export the accounts list as CSV. Shared by the desktop table (AccountsPage) and the
 * mobile card list (AccountsMobileList) so the permission gate and the filename can't
 * drift between the two surfaces.
 *
 * Three rules, from the helpdesk ticket:
 *  1. Master Admin / Admin / Internal Manager only — `can-export-accounts` off /api/me.
 *  2. An Internal Manager exports only their own accounts — already true, because
 *     GET /api/accounts returns `scope: 'assigned'` for non-admins.
 *  3. Applied filters narrow the export — already true, because `rows` is the filtered set.
 */
export const AccountsExportButton = ({
  rows,
  tab,
}: AccountsExportButtonProps) => {
  // Canonical `me` slice. Default-deny: an unloaded /api/me must not flash the button
  // for users who may not export.
  const canExport = useSelector(
    (s: any) => !!s.me?.permissions?.canExportAccounts
  );

  if (!canExport) return null;

  const download = () => {
    const blob = new Blob([accountsCsvText(rows, tab)], {
      type: 'text/csv;charset=utf-8',
    });
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = accountsCsvFilename(tab, new Date());
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
  };

  return (
    // Disabled at zero rows: an over-narrow search would otherwise hand the user a file
    // containing nothing but the header, which reads as a broken export.
    <Button
      variant="ghost"
      icon="download"
      size="sm"
      disabled={rows.length === 0}
      onClick={download}
    >
      Export
    </Button>
  );
};
